import { useState } from "react";
import Modal from "../../components/Modal";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import ExpenseForm from "../entry/ExpenseForm";
import { canChangePending, changePendingEntry, readPendingEntries } from "../../lib/pending-entries";
import { RECEIPT_PREFIX, readPendingReceipts, removePendingReceipt } from "../../lib/pending-receipts";
import { deleteOfflineBlobs } from "../../lib/offline-blobs";
import type { DashboardData, ExpenseInput, Owner } from "../../types";

export default function PendingTransactionDetail({ storageKey, data, profile, onClose }: {
  storageKey: string; data: DashboardData; profile: Owner; onClose: () => void;
}) {
  const isReceipt = storageKey.startsWith(RECEIPT_PREFIX);
  const [original] = useState(() => readPendingEntries().find(item => item.key === storageKey)?.entry);
  const [receipt] = useState(() => readPendingReceipts().find(item => item.key === storageKey)?.receipt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const editable = !!original && canChangePending(storageKey, original);

  async function save(expense: ExpenseInput | null) {
    if (!original || busy) return;
    setBusy(true);
    setError("");
    try {
      await changePendingEntry(storageKey, original, expense, (data.accounts || []).find(a => a.id === expense?.accountId)?.currency);
      onClose();
    } catch (error) { setError((error as Error).message); }
    finally { setBusy(false); }
  }

  async function deleteReceipt() {
    if (!receipt || busy) return;
    setBusy(true);
    setError("");
    try {
      if (receipt.kind === "scan") await deleteOfflineBlobs(receipt.attachments.map((item) => item.id));
      removePendingReceipt(receipt.id);
      onClose();
    } catch (error) { setError((error as Error).message); }
    finally { setBusy(false); }
  }

  if (isReceipt) {
    const label = receipt?.kind === "save"
      ? (receipt.draft.expense.title || "Receipt")
      : "Receipt photos";
    const locked = receipt?.kind === "save" && !!receipt.progress?.transactionId;
    return (
      <>
        <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
        <Modal title="Not synced" onClose={onClose}>
          <p>
            {receipt?.kind === "scan"
              ? "Photos are saved on this device. They will be read when you’re online, then you can finish the receipt."
              : locked
                ? "This receipt has already been sent. Wait for sync confirmation before changing it."
                : "Receipt saved on this device. It will sync automatically when you’re online."}
          </p>
          <p><strong>{label}</strong></p>
          {error && <p role="alert" className="form-error">{error}</p>}
          {receipt && !locked && (
            <button
              type="button"
              className="text-button subtle danger"
              disabled={busy}
              onClick={() =>
                setConfirm({
                  title: "Delete unsynced receipt?",
                  message: "Delete this receipt from this device? It will not be synced.",
                  confirmLabel: "Delete receipt",
                  danger: true,
                  onConfirm: () => deleteReceipt(),
                })
              }
            >
              Delete unsynced receipt
            </button>
          )}
        </Modal>
      </>
    );
  }

  return (
    <>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      <Modal title="Not synced" onClose={onClose}>
        <p>{editable ? "Saved on this device. Changes will sync automatically when you’re online." : "This entry has already been sent. It can be edited after the server confirms its save."}</p>
        {original?.progress?.error && <p role="alert" className="form-error">{original.progress.error}</p>}
        {error && <p role="alert" className="form-error">{error}</p>}
        {original && (
          <ExpenseForm
            data={data}
            profile={profile}
            initialInput={original}
            onSaved={() => {}}
            onReview={(value) => { if (editable) void save(value); }}
            reviewLabel="Save changes"
            externalBusy={busy || !editable}
            freezeFields={!editable}
            autoFocusAmount={false}
          />
        )}
        {editable && (
          <button
            type="button"
            className="text-button subtle danger"
            disabled={busy}
            onClick={() =>
              setConfirm({
                title: "Delete unsynced transaction?",
                message: "Delete this transaction from this device? It will not be synced.",
                confirmLabel: "Confirm delete",
                danger: true,
                onConfirm: () => save(null),
              })
            }
          >
            Delete unsynced transaction
          </button>
        )}
      </Modal>
    </>
  );
}
